import { cn } from '@/lib/utils';
import { shortBuildId } from '@/lib/version-skew';

interface BuildIdLabelProps {
  className?: string;
  /**
   * `compact` (default) is the inventory-app sidebar footer scale this label
   * was built at. `station` mirrors `ClientOrgBadge`'s size prop — larger
   * type for the production floor, read from ~3 metres away.
   */
  variant?: 'compact' | 'station';
}

/**
 * BuildIdLabel — the running frontend build, readable off the screen.
 *
 * Exists so a slow-scan report from the floor arrives with a build id instead of
 * "it takes 4-5 scans". An operator can read it down a phone line or photograph
 * it; nobody has to open devtools.
 *
 * Deliberate choices:
 * - `shortBuildId()` truncates to git's default short-SHA length (7 chars) for
 *   a real build id, but never truncates a `dev-…` id — see lib/version-skew.
 * - No `title` attribute: hover-only info is an anti-pattern on a touch-first
 *   production floor, and the full id already lives in the boot console line
 *   + the Sentry `buildId` tag, so nothing is lost by dropping it.
 * - `select-all` (compact only) so one click grabs the id cleanly.
 * - Rendered unconditionally, including dev (where it shows the `dev-…` form).
 *   A label that hides in dev is a component nobody ever sees fail.
 * - Quiet by design: muted, mono, no background, border, badge or animation. It
 *   is diagnostic metadata, not operator-critical info, so it must disappear
 *   during work while staying legible in a photo.
 * - Language-neutral `#<id>` rendering — no English word to translate.
 *
 * Hosts pass `pointer-events-none` where it floats over a station surface — a
 * scanner-first station must never gain an invisible click target (PRODUCTION_DESIGN §3).
 */
export function BuildIdLabel({ className, variant = 'compact' }: BuildIdLabelProps) {
  const station = variant === 'station';
  return (
    <span
      className={cn(
        'block font-mono tabular-nums',
        station
          ? 'text-sm text-foreground/70'
          : 'text-[11px] text-muted-foreground select-all',
        className
      )}
    >
      #{shortBuildId(__BUILD_ID__)}
    </span>
  );
}
